import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  LinearProgress,
  Collapse,
  IconButton,
} from '@mui/material';
import { createApi } from 'util/ApiCreator';
import { MessageResponse } from 'api/main/data-contracts';
import { MessagePublicController } from 'api/main/MessagePublicController';
import { adminTheme } from 'theme/adminTheme';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const MessagePage = () => {
  const api = useRef(createApi("messagespublic") as MessagePublicController);
  const [messages, setMessages] = useState<MessageResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  useEffect(() => {
    getData();
  }, []);

  const getData = async () => {
    setLoading(true);
    try {
      const data = await api.current.getAll();
      setMessages(data || []);
    } catch (error) {
      console.error("Klaida gaunant pranešimus:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (id: number | undefined) => {
    if (id === undefined) return;
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <Box sx={{ bgcolor: adminTheme.colors.backgroundAlt, minHeight: "100%" }}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 1,
              color: adminTheme.colors.textPrimary,
            }}
          >
            Gauti pranešimai
          </Typography>
          <Typography
            variant="body"
            sx={{ color: adminTheme.colors.textSecondary }}
          >
            Peržiūrėkite gautus pranešimus
          </Typography>
        </Box>

        {loading && <LinearProgress sx={{ mb: 3 }} />}

        {/* Stats Card */}
        <Box sx={{ display: "flex", gap: 3, mb: 4, flexWrap: "wrap" }}>
          <Card
            sx={{
              flex: 1,
              minWidth: 200,
              maxWidth: 300,
              p: 3,
              borderRadius: adminTheme.borderRadius.lg,
              border: `1px solid ${adminTheme.colors.border}`,
              boxShadow: "none",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: adminTheme.colors.textMuted, mb: 1 }}
            >
              Iš viso pranešimų
            </Typography>
            <Typography
              variant="h3"
              sx={{ fontWeight: 700, color: adminTheme.colors.textPrimary }}
            >
              {messages.length}
            </Typography>
          </Card>
        </Box>

        {/* Messages List */}
        {messages.length === 0 && !loading ? (
          <Card
            sx={{
              borderRadius: adminTheme.borderRadius.lg,
              border: `1px solid ${adminTheme.colors.border}`,
              boxShadow: "none",
              p: 6,
              textAlign: "center",
            }}
          >
            <NotificationsIcon
              sx={{ fontSize: 64, mb: 2, color: adminTheme.colors.textMuted, opacity: 0.5 }}
            />
            <Typography variant="h6" sx={{ color: adminTheme.colors.textMuted }}>
              Pranešimų nėra
            </Typography>
            <Typography variant="body2" sx={{ color: adminTheme.colors.textMuted, mt: 1 }}>
              Kai gausite naujų pranešimų, jie bus rodomi čia
            </Typography>
          </Card>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {messages.map((message) => (
              <Card
                key={message.id}
                sx={{
                  borderRadius: adminTheme.borderRadius.lg,
                  border: `1px solid ${adminTheme.colors.border}`,
                  boxShadow: "none",
                  overflow: "hidden",
                  transition: adminTheme.transitions.fast,
                  "&:hover": {
                    borderColor: adminTheme.colors.primary,
                  },
                }}
              >
                {/* Message Header */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 2,
                    cursor: "pointer",
                    bgcolor: expandedId === message.id ? "rgba(99, 102, 241, 0.04)" : "transparent",
                  }}
                  onClick={() => toggleExpand(message.id)}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: adminTheme.borderRadius.sm,
                        bgcolor: "rgba(99, 102, 241, 0.1)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: adminTheme.colors.primary,
                      }}
                    >
                      <NotificationsIcon sx={{ fontSize: 24 }} />
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: adminTheme.colors.textPrimary,
                          fontSize: "1.1rem",
                        }}
                      >
                        {message.subject}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                        <AccessTimeIcon sx={{ fontSize: 14, color: adminTheme.colors.textMuted }} />
                        <Typography variant="caption" sx={{ color: adminTheme.colors.textMuted }}>
                          {message.dateCreated}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <IconButton size="small">
                    {expandedId === message.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>

                {/* Message Content */}
                <Collapse in={expandedId === message.id} timeout="auto" unmountOnExit>
                  <Box
                    sx={{
                      px: 3,
                      pb: 3,
                      pt: 1,
                      borderTop: `1px solid ${adminTheme.colors.border}`,
                    }}
                  >
                    <Box
                      sx={{
                        p: 3,
                        bgcolor: adminTheme.colors.backgroundAlt,
                        borderRadius: adminTheme.borderRadius.sm,
                        "& p": { margin: 0 },
                        "& a": { color: adminTheme.colors.primary },
                      }}
                      dangerouslySetInnerHTML={{ __html: message.content || '' }}
                    />
                  </Box>
                </Collapse>
              </Card>
            ))}
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default MessagePage;
